import defaults from 'lodash/defaults'

import fetchAPI from '@/lib/blog/client'
import { APP_URL, NEAT_DATA_URL, BLOG_IMAGE_URL, BLOG_REDIRECTS } from '@/config'
import { sendError } from '@/utils/rollbar'

const POST_FRAGMENT = `fragment PostFields on Post {
  title
  excerpt
  slug
  date
  modified
  author {
    node {
      name
    }
  }
  featuredImage {
    node {
      sourceUrl
      altText
      mediaDetails {
        width
        height
      }
    }
  }
  categories {
    edges {
      node {
        name
        id
        slug
      }
    }
  }
  tags {
    edges {
      node {
        name
      }
    }
  }
}`

/**
 * This will redirect all WordPress domains to *.neat.com or S3 image domain
 *
 * @param {string} string
 * @return {string} The redirect modified string
 */
const replaceDomain = (string = '') => {
  const appRedirectRegex = (domain) => `${domain}(?!/wp-content)`
  const imageRedirectRegex = (domain) => `${domain}/wp-content`

  const appRedirects = new RegExp(`${BLOG_REDIRECTS.map(domain => appRedirectRegex(domain)).join('|')}`, 'g')
  const imageRedirects = new RegExp(`${BLOG_REDIRECTS.map(domain => imageRedirectRegex(domain)).join('|')}`, 'g')

  return string.replace(appRedirects, APP_URL).replace(imageRedirects, BLOG_IMAGE_URL)
}

const replaceDomainInPost = (data) => {
  if (data?.post) {
    data.post.content = replaceDomain(data.post.content)
    if (data.post.seo) {
      data.post.seo.fullHead = replaceDomain(data.post.seo.fullHead)
      data.post.seo.breadcrumbs = data.post.seo.breadcrumbs.map(breadcrumb => ({
        text: breadcrumb.text,
        url: replaceDomain(breadcrumb.url),
      }))
    }
    if (data.post.featuredImage?.node?.sourceUrl) {
      data.post.featuredImage.node.sourceUrl = replaceDomain(data.post.featuredImage.node.sourceUrl)
    }
  }

  return data
}

export async function getBlogConfig() {
  const defaultConfig = {
    featured_posts: [],
    popular_posts: [],
    recent_posts: [],
    category_page_size: 10,
    revalidate_seconds: 60 * 60, // 1 hour
  }
  try {
    const response = await fetch(`${NEAT_DATA_URL}/blog/configuration.json`)
    const json = await response.json()
    if (json.errors) {
      sendError(json.errors, 'Error fetching blog data from S3')
      return defaultConfig
    }
    return defaults(json, defaultConfig)
  } catch (err) {
    sendError(err, 'Error fetching blog data from S3')
    return defaultConfig
  }
}

export async function getPostBySlug(slug) {
  const data = await fetchAPI(
    `
    ${POST_FRAGMENT}
    query PostBySlug($id: ID!, $idType: PostIdType!) {
      post(id: $id, idType: $idType) {
        ...PostFields
        }
        }
        `,
    {
      variables: {
        id: slug,
        idType: 'SLUG',
      },
    },
  )
  return replaceDomainInPost(data)
}
export async function getPostBySlugWithSEO(slug) {
  const data = await fetchAPI(
    `
    ${POST_FRAGMENT}
    query PostBySlug($id: ID!, $idType: PostIdType!) {
      post(id: $id, idType: $idType) {
        ...PostFields
        content
        seo {
          fullHead
          readingTime
          metaDesc
          breadcrumbs {
            text
            url
          }
        }
      }
    }
  `,
    {
      variables: {
        id: slug,
        idType: 'SLUG',
      },
    },
  )

  return replaceDomainInPost(data)
}

export async function getFeaturedPosts(slugs = []) {
  const posts = await Promise.all(
    slugs.map(async slug => {
      const { post } = await getPostBySlug(slug)
      return post
    }),
  )

  return posts.filter(post => post)
}

export async function getPopularPosts(slugs = []) {
  const posts = await Promise.all(
    slugs.map(async slug => {
      const { post } = await getPostBySlug(slug)
      return post
    }),
  )

  return posts.filter(post => post)
}

export async function getRecentPosts(slugs = []) {
  const posts = await Promise.all(
    slugs.map(async slug => {
      const { post } = await getPostBySlug(slug)
      return post
    }),
  )

  return posts.filter(post => post)
}

export async function getPostsForCategory(slug, size = 10000, cursor = '') {
  const data = await fetchAPI(
    `
    query PostsForCategory($id: ID!, $idType: CategoryIdType!, $after: String = "", $first: Int = 5) {
      category(id: $id, idType: $idType) {
        slug
        name
        posts(first: $first, after: $after, where: {orderby: {field: DATE, order: DESC}}) {
          nodes {
            date
            slug
            excerpt
            title
            featuredImage {
              node {
                sourceUrl
                altText
              }
            }
            categories {
              edges {
                node {
                  name
                  id
                  slug
                }
              }
            }
          }
          edges {
            node {
              slug
            }
          }
        }
      }
    }
    `,
    {
      variables: {
        id: slug,
        idType: 'SLUG',
        first: size,
        after: cursor,
      },
    },
  )

  return data
}

export async function getPosts() {
  try {
    const data = await fetchAPI(
      `query {
        posts(first: 10000) {
          nodes {
            id
            slug
            modified
          }
        }
      }
    `,
      {
        variables: {},
      },
    )
    return data.posts?.nodes ?? []
  } catch (err) {
    return []
  }
}
